import * as React from "react"

import { cn } from "@/lib/utils"

const INPUT_DEFAULT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"

/**
 * The touch surfaces' underline field (docs/MOBILE.md): no box, a 1px input
 * line under the text that turns foreground on focus, 16px text.
 */
const INPUT_UNDERLINE_CLASS =
  "flex w-full rounded-none border-0 border-b border-input bg-transparent px-0 py-2 text-base font-normal text-foreground placeholder:text-muted-foreground focus-visible:border-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"

export interface InputProps extends React.ComponentProps<"input"> {
  /**
   * `default` is the boxed desktop field. `underline` is the boxless touch
   * field. `search` is the touch search: a foreground underline with a leading
   * icon slot; `className` then styles the wrapping row, not the input. The
   * line stays 1px on focus (as the board draws it); focus shows as the icon
   * turning foreground, and keyboard focus adds an outline ring around the
   * whole field (with or without an icon), never as a second line.
   */
  variant?: "default" | "underline" | "search"
  /** Leading icon for the `search` variant (aria-hidden). */
  leadingIcon?: React.ReactNode
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant = "default", leadingIcon, ...props }, ref) => {
    if (variant === "search") {
      return (
        <div
          className={cn(
            "flex h-12 w-full items-center gap-2 border-b border-foreground text-foreground [&_svg]:size-[18px] [&_svg]:shrink-0 [&_svg]:text-muted-foreground focus-within:[&_svg]:text-foreground has-[:focus-visible]:rounded-sm has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-4 has-[:focus-visible]:outline-ring",
            className
          )}
        >
          {leadingIcon ? <span aria-hidden="true" className="flex">{leadingIcon}</span> : null}
          <input
            type={type ?? "search"}
            className="min-w-0 flex-1 border-0 bg-transparent text-base font-normal text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            ref={ref}
            {...props}
          />
        </div>
      )
    }
    return (
      <input
        type={type}
        className={cn(
          variant === "underline" ? INPUT_UNDERLINE_CLASS : INPUT_DEFAULT_CLASS,
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
